import { inject, Injectable, makeEnvironmentProviders } from '@angular/core'
import {
  provideServiceWorker,
  SwUpdate,
  VersionInstallationFailedEvent,
} from '@angular/service-worker'

import { filter, map, Observable, of, take, timer } from 'rxjs'

import { AppUpdateInfo, IUpdateManager, UPDATE_MANAGER } from '@ti/core/contracts/update'

@Injectable()
export class BrowserUpdateManager implements IUpdateManager {
  public readonly updateType = 'SERVICE_WORKER'
  protected readonly swUpdate = inject(SwUpdate, { optional: true })

  public readonly versionReady$: Observable<AppUpdateInfo> =
    this.swUpdate?.versionUpdates.pipe(
      filter((e) => e.type === 'VERSION_READY'),
      map(() => ({
        updateType: 'SERVICE_WORKER',
        updateAvailability: 'UPDATE_AVAILABLE',
      })),
    ) ?? of()

  public constructor() {
    Promise.resolve()
      .then(() => this.init())
      .catch((e) => console.warn('Cannot init BrowserUpdateManager', e))
  }

  public async getAppUpdateInfo(): Promise<AppUpdateInfo> {
    if (!this.swUpdate?.isEnabled) {
      return {
        updateType: 'SERVICE_WORKER',
        updateAvailability: 'UNKNOWN',
      }
    }

    try {
      const updateFound = await this.swUpdate.checkForUpdate()
      return {
        updateType: 'SERVICE_WORKER',
        updateAvailability: updateFound ? 'UPDATE_AVAILABLE' : 'UPDATE_NOT_AVAILABLE',
      }
    } catch {
      return {
        updateType: 'SERVICE_WORKER',
        updateAvailability: 'UNKNOWN',
      }
    }
  }

  public async execUpdate() {
    if (!this.swUpdate?.isEnabled) return

    await this.swUpdate.activateUpdate()
    window.location.reload()
  }

  protected async init() {
    const swUpdate = this.swUpdate
    if (!swUpdate?.isEnabled) return

    swUpdate.versionUpdates
      .pipe(
        filter(
          (e): e is VersionInstallationFailedEvent => e.type === 'VERSION_INSTALLATION_FAILED',
        ),
      )
      .subscribe((event) => {
        console.warn(`SW Installation failed: ${event.error}`)
        this.clearCachesAndRemoveServiceWorker()
      })

    // The SW is broken. Reload the page to bypass it and get fresh assets.
    swUpdate.unrecoverable.subscribe((event) => {
      console.error('SW Unrecoverable state:', event.reason)
      this.clearCachesAndRemoveServiceWorker()
      window.location.reload()
    })

    timer(2000)
      .pipe(take(1))
      .subscribe(() =>
        swUpdate.checkForUpdate().catch((e) => console.warn('SW update check failed', e)),
      )
  }

  private async clearCachesAndRemoveServiceWorker() {
    // Clear all Angular-specific caches
    // if ('caches' in window) {
    //   const cacheNames = await caches.keys()
    //   const ngswCaches = cacheNames.filter((name) => name.startsWith('ngsw:'))
    //   await Promise.all(ngswCaches.map((name) => caches.delete(name)))
    // }
    // Unregister all service workers for the app
    // const registrations = await navigator.serviceWorker.getRegistrations()
    // for (const registration of registrations) {
    //   await registration.unregister()
    // }
  }
}

export const provideBrowserServiceWorker = (script: string, options: { enabled: boolean }) =>
  makeEnvironmentProviders([
    provideServiceWorker(script, {
      enabled: options.enabled,
      registrationStrategy: 'registerWhenStable:30000',
    }),
    { provide: UPDATE_MANAGER, useClass: BrowserUpdateManager },
  ])
